import { useQuery } from '@tanstack/react-query';
import type { ReportPeriod, SentimentReportQuery } from '@discovr/types';
import {
  audienceReportOptions,
  creatorContentReportOptions,
  generalReportOptions,
  sentimentReportOptions,
} from '../api/reportQueries';
import type { CreatorContentParams } from '../queryKeys';

const CAMPAIGN_ID_KEY_INDEX = 1;

/** Carries the previous data over a period switch, but never from another campaign's report. */
function keepPreviousWithinCampaign(campaignId: string) {
  return <T>(previousData: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined =>
    previousQuery?.queryKey[CAMPAIGN_ID_KEY_INDEX] === campaignId ? previousData : undefined;
}

/** Keeps the previous period's data on screen while a new period of the same campaign loads. */
export function useGeneralReport(campaignId: string, period?: ReportPeriod) {
  return useQuery({
    ...generalReportOptions(campaignId, period),
    enabled: Boolean(campaignId),
    placeholderData: keepPreviousWithinCampaign(campaignId),
  });
}

export function useSentimentReport(campaignId: string, query: SentimentReportQuery) {
  return useQuery({
    ...sentimentReportOptions(campaignId, query),
    enabled: Boolean(campaignId),
    placeholderData: keepPreviousWithinCampaign(campaignId),
  });
}

export function useAudienceReport(campaignId: string) {
  return useQuery({ ...audienceReportOptions(campaignId), enabled: Boolean(campaignId) });
}

/** Keeps the previous page, size or sort on screen while the next one loads, within the same campaign. */
export function useCreatorContentReport(campaignId: string, params: CreatorContentParams) {
  return useQuery({
    ...creatorContentReportOptions(campaignId, params),
    enabled: Boolean(campaignId),
    placeholderData: keepPreviousWithinCampaign(campaignId),
  });
}
